import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";

import { Disclose, EmptyState, Loading, Permission, Refusal, hostConfig, kernel, kernelPost } from "@lopie/kernel-ui";

import ConversationList from "./ConversationList.jsx";
import ConversationThread from "./ConversationThread.jsx";
import NewTextDialog from "./NewTextDialog.jsx";
import TakeoverPanel from "./TakeoverPanel.jsx";
import { fetchAllPages } from "./lib/kernelPaging.js";
import { buildConversations, indexPeopleByPhone } from "./lib/conversations.js";

/**
 * Textline.
 *
 * Contacts are Person, texts are Message, both read from the kernel under
 * this app's own manifest — Person and Message tagged customer, employee or
 * vendor. A conversation is every Message sharing one counterparty number,
 * oldest to newest; the counterparty is read off `direction` (inbound's
 * from_number, outbound's to_number), so grouping never has to know this
 * org's own Twilio number.
 *
 * Collection paths come from GET /schema rather than being hardcoded, per
 * the kernel UI kit's own rule: render from the kernel, not from a copy of
 * it. Person/Message are what this app inherently is, so declaring those two
 * types isn't a hardcoded copy — where they live in the kernel's storage is.
 */
const FALLBACK_COLLECTIONS = { Person: "people", Message: "messages" };

export default function App({ token, claims, onSignOut }) {
  const [collections, setCollections] = useState(null);
  const [personsState, setPersonsState] = useState({ status: "loading" });
  const [messagesState, setMessagesState] = useState({ status: "loading" });
  const [selected, setSelected] = useState(null);
  const [newTextOpen, setNewTextOpen] = useState(false);
  const [pendingEchoes, setPendingEchoes] = useState([]);

  const get = useMemo(() => kernel(token), [token]);
  const post = useMemo(() => kernelPost(token), [token]);
  const conf = hostConfig();
  const ownApi = conf?.appTextlineApi || "";

  const own = useCallback(
    async (path, init) => {
      const r = await fetch(ownApi + path, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          "content-type": "application/json",
          ...(init?.headers || {}),
        },
      });
      const text = await r.text();
      let body = null;
      try {
        body = JSON.parse(text);
      } catch {
        body = null;
      }
      if (!r.ok) throw Object.assign(new Error("refused"), { status: r.status, body });
      return body;
    },
    [ownApi, token],
  );

  // Resolve collection paths once, off the live schema; fall back to this
  // org's current names only if that read itself fails.
  useEffect(() => {
    let cancelled = false;
    get("/schema")
      .then((s) => {
        if (cancelled) return;
        const types = s.types || [];
        const person = types.find((t) => t.type === "Person")?.collection;
        const message = types.find((t) => t.type === "Message")?.collection;
        setCollections({
          Person: person || FALLBACK_COLLECTIONS.Person,
          Message: message || FALLBACK_COLLECTIONS.Message,
        });
      })
      .catch(() => {
        if (!cancelled) setCollections(FALLBACK_COLLECTIONS);
      });
    return () => {
      cancelled = true;
    };
  }, [get]);

  const loadPersons = useCallback(async () => {
    if (!collections) return;
    setPersonsState({ status: "loading" });
    try {
      const { items, permittedTags, declaredTags } = await fetchAllPages(get, `/${collections.Person}`);
      setPersonsState({ status: "ok", items, permittedTags, declaredTags });
    } catch (e) {
      setPersonsState({ status: "error", error: e });
    }
  }, [get, collections]);

  const loadMessages = useCallback(async () => {
    if (!collections) return;
    setMessagesState({ status: "loading" });
    try {
      const { items, permittedTags, declaredTags } = await fetchAllPages(get, `/${collections.Message}`);
      setMessagesState({ status: "ok", items, permittedTags, declaredTags });
    } catch (e) {
      setMessagesState({ status: "error", error: e });
    }
  }, [get, collections]);

  useEffect(() => {
    if (!collections) return;
    loadPersons();
    loadMessages();
  }, [collections, loadPersons, loadMessages]);

  const byPhone = useMemo(
    () => (personsState.status === "ok" ? indexPeopleByPhone(personsState.items) : new Map()),
    [personsState],
  );

  const conversations = useMemo(() => {
    if (messagesState.status !== "ok") return [];
    const base = buildConversations(messagesState.items, byPhone);
    if (pendingEchoes.length === 0) return base;
    // A send just accepted by the kernel will not appear as a Message record
    // until the next hourly poll. Echoed locally, marked pending, so the
    // thread does not look like it swallowed what was just typed.
    const byNumber = new Map(base.map((c) => [c.number, c]));
    for (const echo of pendingEchoes) {
      const msg = { id: echo.id, direction: "outbound", body: echo.body, sentAt: echo.sentAt, pending: true };
      const existing = byNumber.get(echo.number);
      if (existing) {
        byNumber.set(echo.number, {
          ...existing,
          messages: [...existing.messages, msg],
          lastMessage: msg,
          awaitingReply: false,
        });
      } else {
        byNumber.set(echo.number, {
          number: echo.number,
          person: byPhone.get(echo.number) || null,
          messages: [msg],
          lastMessage: msg,
          awaitingReply: false,
        });
      }
    }
    return Array.from(byNumber.values()).sort((a, b) => new Date(b.lastMessage.sentAt) - new Date(a.lastMessage.sentAt));
  }, [messagesState, byPhone, pendingEchoes]);

  const selectedConversation = conversations.find((c) => c.number === selected) || null;

  const sendMessage = useCallback(
    async (to, body) => {
      const idempotencyKey = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${to}`;
      await post("/actions/message.send", { to, body, idempotency_key: idempotencyKey });
      setPendingEchoes((prev) => [
        ...prev,
        { id: `pending-${idempotencyKey}`, number: to, body, sentAt: new Date().toISOString() },
      ]);
      setSelected(to);
    },
    [post],
  );

  const reload = useCallback(() => {
    loadPersons();
    loadMessages();
  }, [loadPersons, loadMessages]);

  const stillLoading =
    !collections || (personsState.status === "loading" && messagesState.status === "loading");
  const bothFailed = personsState.status === "error" && messagesState.status === "error";

  if (stillLoading) {
    return <Loading label="Reading contacts and messages" minHeight="60vh" />;
  }

  if (bothFailed) {
    return (
      <Box sx={{ p: 3 }}>
        <Refusal failure={messagesState.error} actor="Textline" onRetry={reload} />
      </Box>
    );
  }

  const permittedTags = messagesState.permittedTags || personsState.permittedTags || [];
  const declaredTags = messagesState.declaredTags ?? personsState.declaredTags ?? null;

  return (
    <Stack spacing={2} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0, height: "100vh", boxSizing: "border-box" }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap" }}>
        <Typography variant="h1">Textline</Typography>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {claims?.username || ""}
          </Typography>
          <Typography
            component="button"
            variant="body2"
            onClick={onSignOut}
            sx={{ background: "none", border: 0, cursor: "pointer", color: "primary.main" }}
          >
            Sign out
          </Typography>
        </Stack>
      </Stack>

      <Permission
        gate="Governed by this app's reviewed manifest: Person and Message, tagged customer, employee or vendor, intersected with your own groups."
        groups={claims?.["cognito:groups"] || []}
        effective={{ permitted: permittedTags, declared: declaredTags }}
      />

      {personsState.status === "error" ? (
        <Refusal failure={personsState.error} actor="Textline · Person" onRetry={loadPersons} />
      ) : null}
      {messagesState.status === "error" ? (
        <Refusal failure={messagesState.error} actor="Textline · Message" onRetry={loadMessages} />
      ) : null}

      <TakeoverPanel own={own} />

      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={() => setNewTextOpen(true)}>
          New text
        </Button>
      </Stack>

      {messagesState.status === "ok" && conversations.length === 0 ? (
        <EmptyState title="No conversations yet">
          <Typography variant="body2">
            Texts to and from this org's number are polled in hourly. Nothing has come through yet, or this app
            cannot see any — check the panel above.
          </Typography>
        </EmptyState>
      ) : null}

      {messagesState.status === "ok" && conversations.length > 0 ? (
        <Stack
          direction="row"
          sx={{ flex: 1, minHeight: 0, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}
        >
          <Box sx={{ width: 320, flexShrink: 0, borderRight: "1px solid", borderColor: "divider", overflowY: "auto" }}>
            <ConversationList conversations={conversations} selected={selected} onSelect={setSelected} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {selectedConversation ? (
              <ConversationThread conversation={selectedConversation} onSend={sendMessage} />
            ) : (
              <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Select a conversation
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      ) : null}

      <NewTextDialog open={newTextOpen} onClose={() => setNewTextOpen(false)} onSend={sendMessage} byPhone={byPhone} />

      <Disclose label="What this does not prove">
        <Stack spacing={1.5} sx={{ maxWidth: "62ch" }}>
          <Typography variant="body2" color="text.secondary">
            Messages are polled into the kernel hourly, so a text sent or received in the last hour may not be
            listed yet. A message you just sent is shown here right away, marked "sending", until the next poll
            confirms it as a Message record.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A conversation matches a contact by exact phone number. Two Person records sharing one number show as
            whichever one this app read first.
          </Typography>
        </Stack>
      </Disclose>
    </Stack>
  );
}
